import React, { createContext, useState, ReactNode } from "react";
import { getObjectRequester } from "../../utils/localStorage/localStorage";
import { ObjectRequester } from "../../utils/localStorage/LocalStorage.interface";
import appConfig from "../../constants/config";
import { postApiAxios } from "../../utils/apiClient";
import endPoint from "../../constants/endPoint";

// Define types for authentication context
interface AuthContextType {
  currentUser: ObjectRequester | undefined;
  loadEmployeeProfile: () => Promise<void>;
  unImpersonate: () => Promise<void>;
  impersonateUser: (employeeId: string) => Promise<void>;
  logout: () => void;
  isLoadingAuth: boolean;
  isImpersonating: boolean;
  isImpersonated: any;
  keyRefresh: number; //The key supports to refresh app
  refreshAuth(): void;
}
const impersonatedKey = "IsImpersonated";

// Create AuthContext
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

// Create a provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<ObjectRequester | undefined>(
    () => {
      return getObjectRequester() ?? undefined;
    }
  );
  const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(false);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [keyRefresh, setKeyRefresh] = useState<number>(0);

  const setUserRoles = async (personnel_number: string, user_role: string) => {
    try {
      const userRoles = await postApiAxios(endPoint.GetUserRoles, {
        personnel_number: personnel_number,
        user_roles: user_role,
      });
      if (userRoles)
        localStorage.setItem("RoleChecking", JSON.stringify(userRoles));
    } catch {}
  };
  const loadAuth = async () => {
    setIsLoadingAuth(true);
    try {
      const debugUserName = appConfig.connectionString.DebugUserName;
      const params = {
        employee_name:
          debugUserName !== ""
            ? debugUserName
            : localStorage.getItem("ADUsername"),
      };

      const processedParam = JSON.parse(JSON.stringify(params));
      const isImpersonated = localStorage.getItem(impersonatedKey);

      if (!isImpersonated || +isImpersonated !== 1) {
        const responseData = await postApiAxios(
          endPoint.GetEmployeeProfile,
          processedParam
        );
        await setUserRoles(
          responseData[0].personnel_number,
          responseData[0].user_roles
        );
        localStorage.setItem("employee_id", responseData[0].personnel_number);
        localStorage.setItem("country_code", responseData[0].country_code);
        localStorage.setItem("company_code", responseData[0].company_code);
        localStorage.setItem("language_code", responseData[0].uI_LANGUAGE_CODE);
        localStorage.setItem("objCreator", JSON.stringify(responseData[0]));
        localStorage.setItem("objRequestor", JSON.stringify(responseData[0]));
        localStorage.setItem(
          "Form_Simplification_Company",
          appConfig.FormSimplificationCompany
        );

        setCurrentUser(responseData[0]);
      }
    } catch (error) {
      console.log("Error when loading auth", error);
    } finally {
      setIsLoadingAuth(false);
    }
  };

  const impersonateUser = async (employeeId: string) => {
    setIsLoadingAuth(true);

    try {
      const processedParam = JSON.parse(
        JSON.stringify({
          employee_id: employeeId,
        })
      );

      const responseData = await postApiAxios(
        endPoint.GetEmployeeProfile,
        processedParam
      );

      await setUserRoles(
        responseData[0].personnel_number,
        responseData[0].user_roles
      );

      localStorage.setItem("objRequestor", JSON.stringify(responseData[0]));
      localStorage.setItem(impersonatedKey, "1");

      setCurrentUser(responseData[0]);
      setIsImpersonating(true);
      setKeyRefresh((prev) => prev + 1);
    } catch (error) {
      console.log("Error when impersonating", error);
    } finally {
      setIsImpersonating(false);
    }
  };

  const unImpersonate = async () => {
    localStorage.removeItem(impersonatedKey);
    await loadAuth();
    setKeyRefresh((prev) => prev + 1);
  };

  const refreshAuth = async () => {
    await loadAuth();
  };
  const logout = () => {
    //To do
  };

  const isImpersonated = localStorage.getItem(impersonatedKey);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loadEmployeeProfile: loadAuth,
        logout,
        impersonateUser,
        isImpersonating,
        isLoadingAuth,
        keyRefresh,
        unImpersonate,
        isImpersonated: isImpersonated && +isImpersonated === 1,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
